import { useState, useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Button,
  Spinner,
  makeStyles,
  mergeClasses,
  tokens,
  Text,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  ArrowUploadRegular,
  DeleteRegular,
} from '@fluentui/react-icons';
import { getFileTypeIcon } from '../../file';
import { uploadFile } from '@moko/utils/src/uploadHelper';
import { useFileDownload } from '../../hooks/useFileDownload';
import type { IUploadFileResponse } from '@moko/utils/src/interfaces/UploadFileInterfaces';
import { useOtherInfoContext } from '../OtherInfoContext';
import { FieldWrapper } from './FieldWrapper';
import type { FieldComponentProps } from './types';

const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface FileValue {
  url: string;
  name: string;
}

const parseFileValue = (raw: any): FileValue | null => {
  if (!raw) return null;
  if (typeof raw === 'object' && raw.url) return raw as FileValue;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.url) return parsed as FileValue;
    } catch {
      /* not JSON, treat as plain url */
      if (raw.startsWith('http') || raw.startsWith('data:')) {
        return { url: raw, name: 'file' };
      }
    }
  }
  return null;
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  dropzone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalS,
    boxSizing: 'border-box',
    width: '100%',
    height: '100px',
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    transitionProperty: 'border-color, background-color',
    transitionDuration: tokens.durationNormal,
    ':hover': {
      border: `2px dashed ${tokens.colorBrandStroke1}`,
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  dropzoneActive: {
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    minWidth: 0,
    overflow: 'hidden',
    gap: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomColor: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalSNudge,
    height: '40px',
  },
  fileIcon: {
    fontSize: '20px',
    flexShrink: 0,
    display: 'flex',
  },
  fileName: {
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const FileField = ({
  field,
  fieldValue,
  isFieldDisabled,
  validationMessage,
}: FieldComponentProps) => {
  const { handleOtherInfoChange } = useOtherInfoContext();
  const { download, downloading } = useFileDownload();
  const [uploading, setUploading] = useState(false);
  const styles = useStyles();

  const fileData = useMemo(() => parseFileValue(fieldValue), [fieldValue]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;

        if (!navigator.onLine) {
          handleOtherInfoChange(
            field.name,
            JSON.stringify({ url: dataUrl, name: file.name })
          );
          return;
        }

        const abortController = new AbortController();
        setUploading(true);
        uploadFile(abortController, file.name, dataUrl)
          .then((data: IUploadFileResponse) => {
            handleOtherInfoChange(
              field.name,
              JSON.stringify({ url: data.url, name: file.name })
            );
          })
          .finally(() => setUploading(false));
      };
      reader.readAsDataURL(file);
    },
    [field.name, handleOtherInfoChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    multiple: false,
    maxSize: MAX_FILE_SIZE,
    disabled: isFieldDisabled,
  });

  const handleRemove = useCallback(() => {
    handleOtherInfoChange(field.name, '');
  }, [field.name, handleOtherInfoChange]);

  const handleDownload = useCallback(() => {
    if (!fileData) return;
    download(fileData.name, fileData.url);
  }, [fileData, download]);

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      {fileData ? (
        <div className={styles.fileRow}>
          <span className={styles.fileIcon}>
            {getFileTypeIcon(fileData.name)}
          </span>
          <Text className={styles.fileName} size={200} truncate>
            {fileData.name}
          </Text>
          <Button
            appearance="subtle"
            icon={
              downloading ? <Spinner size="tiny" /> : <ArrowDownloadRegular />
            }
            size="small"
            disabled={downloading}
            onClick={handleDownload}
          />
          {!isFieldDisabled && (
            <Button
              appearance="subtle"
              icon={<DeleteRegular />}
              size="small"
              disabled={downloading}
              onClick={handleRemove}
            />
          )}
        </div>
      ) : !isFieldDisabled ? (
        <div
          {...getRootProps({
            className: mergeClasses(
              styles.dropzone,
              isDragActive && styles.dropzoneActive
            ),
          })}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <Spinner size="tiny" />
          ) : (
            <>
              <ArrowUploadRegular fontSize={24} />
              <Text size={200}>Drop file here or click to upload</Text>
              <Text
                size={100}
                style={{ color: tokens.colorNeutralForeground4 }}
              >
                .pdf, .docx, .xlsx (max 10 MB)
              </Text>
            </>
          )}
        </div>
      ) : null}
    </FieldWrapper>
  );
};
